import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { BaseSiteService, CmsService, GlobalMessageService, GlobalMessageType, LanguageService } from '@spartacus/core';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { DataLayerService } from 'src/app/shared/services/data-layer.service';
import { SharedService } from '../../../shared/services/shared.service';
import { CustomRegistrationService } from '../../custom-registration.service';
import { globalErrorMsgEng, globalErrorMsgFr, siteLanguagesEng } from '../../registration.constants';

const alternateEmail = 'alternateEmailInfo.alternateEmail';
const confirmAlternateEmail = 'alternateEmailInfo.confirmAlternateEmail';
const emailRegexExp = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,15}$/;

@Component({
    selector: 'app-user-information',
    templateUrl: './user-information.component.html',
    styleUrls: ['./user-information.component.scss'],
    standalone: false
})
export class UserInformationComponent implements OnInit, OnDestroy {
  @Input() formDetails: FormGroup;
  @Input() isLoggedIn: any;

  accNo: string;
  finalNeedAss: any;
  creditInvoice: any;
  header: any;
  userInfoForm: FormGroup;
  captchaResp: string;
  errorMsg = false;
  isEmailConsent: boolean[] = [true, false];
  contactRole: any[] = [];
  altEmailSection = false;
  accountData: any;
  errorMessageText: string = globalErrorMsgEng;
  userContactRoleSub: Subscription;
  accountNoSub: Subscription;
  userRegSub: Subscription;
  confirmationMessage: { title: string; content: string } = {
    title: '',
    content: '',
  };
  isUsernameExists = false;
  isEmailExists = false;
  errorMessages: string[] = [
    'b2bCustomer.register.userName.exists',
    'b2bCustomer.register.email.exists',
  ];
  baseSiteId: any;
  constructor(
    private readonly accountRegService: CustomRegistrationService,
    private readonly cdr: ChangeDetectorRef,
    private readonly sharedService: SharedService,
    private readonly router: Router,
    private readonly languageService: LanguageService,
    private readonly globalMessage: GlobalMessageService,
    private baseSite: BaseSiteService,
    private readonly cmsData: CmsService,
    private _dataLayerService: DataLayerService
  ) { }

  ngOnInit(): void {
    //GTM
    this._dataLayerService.addtoDataLayer('userReg_FormEvent', '', '', '', 'form_step', 'user');
    this.header = 'userInformation';
    this.creditInvoice = 'invoiceTable';
    this.baseSite.getActive().pipe(take(1)).subscribe((res) => (this.baseSiteId = res));
    this.accNo = this.formDetails.get('accountNumber').value;
    window.scroll(0, 0);
    this.languageService.getActive().subscribe((langRes) => {
      this.errorMessageText =
        langRes === siteLanguagesEng ? globalErrorMsgEng : globalErrorMsgFr;
    });

    this.finalNeedAss = this.cmsData.getComponentData('NeedAssistanceComponent');

    const param = {
      accountnumber: this.accNo
    }
    this.accountNoSub = this.accountRegService
      .getAccountData(param)
      .subscribe((res) => {
        if (res) {
          this.accountData = res;
          this.cdr.detectChanges();
        }
      });
    this.accountRegService.getContactRole({
      enumerationType: 'UserRole',
      fields: 'DEFAULT',
    });
    const maxlengthFourty = 40;
    this.userInfoForm = new FormGroup({
      userInfoDetails: new FormGroup({
        userName: new FormControl('', [
          Validators.required,
          Validators.pattern('^[a-zA-Z0-9]+$'),
          Validators.minLength(1),
          Validators.maxLength(10),
        ]),
      }),
      firstName: new FormControl('', [
        Validators.required,
        Validators.pattern("^[a-zA-Z][a-zA-Z ']*$"),
        Validators.maxLength(maxlengthFourty),
      ]),
      lastName: new FormControl('', [
        Validators.required,
        Validators.pattern("^[a-zA-Z][a-zA-Z ']*$"),
        Validators.maxLength(maxlengthFourty),
      ]),
      role: new FormControl(null, [Validators.required]),
      emailConsent: new FormControl(this.isEmailConsent[1], [
        Validators.required,
      ]),
      alternateEmailInfo: new FormGroup({
        alternateEmail: new FormControl(''),
        confirmAlternateEmail: new FormControl(''),
      }),
    });

    this.userContactRoleSub = this.accountRegService.contactRoleData$.subscribe(
      (response) => {
        if (response !== false) {
          this.contactRole = response;
        }
      }
    );

    this.userInfoForm.get('emailConsent').valueChanges.subscribe((value) => {
      if (value === false) {
        this.deleteAltEmailSection();
      }
    });

    this.accountRegService.existUserInfo.subscribe((data) => {
      if (data) {
        this.userInfoForm.get('userInfoDetails.userName').setValue(data.userName);
        this.userInfoForm.get('firstName').setValue(data.firstName);
        this.userInfoForm.get('lastName').setValue(data.lastName);
        if (data.userName)
          this.userInfoForm.get('userInfoDetails.userName').disable();
        this.cdr.detectChanges();
      }
    })
  }

  setUserDetails(formDta, userDetails): any {
    for (const key in formDta) {
      if (
        key !== 'userInfoDetails' &&
        key !== 'emailInfo' &&
        key !== 'alternateEmailInfo'
      ) {
        userDetails[key] = formDta[key];
      }
    }
    return userDetails;
  }

  submitUserInfo() {
    this.userInfoForm.markAllAsTouched();
    if (this.userInfoForm.valid) {
      let userDetails = {};
      this.userInfoForm.get('userInfoDetails.userName').setValue(this.userInfoForm.get('userInfoDetails.userName').value.toLowerCase());
      const formDta = { ...this.userInfoForm.value };
      if (!!formDta) {
        userDetails = this.setUserDetails(formDta, userDetails);
      }
      const userData = {
        ...this.formDetails.getRawValue(),
        ...userDetails,
        ...this.userInfoForm.get('userInfoDetails').value,
        ...this.userInfoForm.get('alternateEmailInfo').value,
        code: '',
        phoneNumber: '',
        profileUpdateType: '',
        smsConsent: '',
        termsAndConditions: true,
        titleCode: '',
        uid: '',
        existingUserCheck: this.sharedService.getLocalStorage('existingUserCheck')
      };
      if (this.sharedService.getLocalStorage('userUUID'))
        userData['uuid'] = this.sharedService.getLocalStorage('userUUID');
      delete userData?.recaptchaReactive;
      this.confirmationMessage.title = userData.firstName;
      this.createNewUser(userData);
    } else {
      window.scroll(0, 0);
      this.globalMessage.add(
        this.errorMessageText,
        GlobalMessageType.MSG_TYPE_ERROR
      );
    }
  }

  createNewUser(userData) {
    this.sharedService.setLocalStorage('email', userData.email);
    this.userRegSub = this.accountRegService
      .postUserRegistrationInfo(userData, this.isLoggedIn)
      .subscribe(
        (res) => {
          if (res != null) {
            localStorage.setItem('usermeta', JSON.parse(JSON.stringify(res.janrainIdentifier)));
            const completeUserData = {
              header: 'confirmation',
              msgData: this.confirmationMessage,
            };
            this.sharedService.setLocalStorage('userName', userData.userName);
            !!localStorage.getItem('userConfirmationData')
              ? localStorage.removeItem('userConfirmationData')
              : null;
            localStorage.setItem(
              'userConfirmationData',
              JSON.stringify(completeUserData)
            );
            this.sharedService.setSessionStorage(
              'userConfirmationData',
              completeUserData
            );
            this.router.navigate(['/customer-register/confirm']);
          }
        },
        (error) => {
          this.isUsernameExists = false;
          this.isEmailExists = false;
          if (!!error?.error?.errors && error?.error?.errors.length > 0) {
            for (const errorItem of error?.error?.errors) {
              if (errorItem?.message === this.errorMessages[0]) {
                this.isUsernameExists = true;
                this.userInfoForm
                  .get('userInfoDetails.userName')
                  .markAsUntouched();
              } else if (errorItem?.message === this.errorMessages[1]) {
                this.isEmailExists = true;
              }
            }
            this.cdr.detectChanges();
            if (this.isEmailExists || this.isUsernameExists) {
              this.globalMessage.remove(GlobalMessageType.MSG_TYPE_ERROR);
              this.globalMessage.add(
                this.errorMessageText,
                GlobalMessageType.MSG_TYPE_ERROR
              );
            }
            window.scroll(0, 0);

            this._dataLayerService.addtoDataLayer('userReg_FormEvent', '', '', '', 'form_error', 'user');
          }
        }
      );
  }

  addAlternateEmail() {
    this.altEmailSection = true;
    this.userInfoForm
      .get('alternateEmailInfo')
      .setValidators(this.alternateEmailMatchValidator);
    this.userInfoForm
      .get(alternateEmail)
      .setValidators([Validators.required, Validators.pattern(emailRegexExp)]);
    this.userInfoForm
      .get(confirmAlternateEmail)
      .setValidators([Validators.required, Validators.pattern(emailRegexExp)]);
    this.userInfoForm.get(alternateEmail).updateValueAndValidity();
    this.userInfoForm.get(confirmAlternateEmail).updateValueAndValidity();
  }

  deleteAltEmailSection() {
    this.userInfoForm.patchValue(
      {
        alternateEmailInfo: {
          alternateEmail: null,
          confirmAlternateEmail: null,
        },
      },
      { emitEvent: false }
    );
    this.userInfoForm.get('alternateEmailInfo').clearValidators();
    this.userInfoForm.get(alternateEmail).clearValidators();
    this.userInfoForm.get(confirmAlternateEmail).clearValidators();
    this.userInfoForm.get(alternateEmail).markAsUntouched();
    this.userInfoForm.get(alternateEmail).markAsPristine();
    this.userInfoForm.get(confirmAlternateEmail).markAsPristine();
    this.userInfoForm.get(confirmAlternateEmail).markAsUntouched();
    this.userInfoForm.get(alternateEmail).updateValueAndValidity();
    this.userInfoForm.get(confirmAlternateEmail).updateValueAndValidity();
    this.userInfoForm.get('alternateEmailInfo').updateValueAndValidity();
    this.altEmailSection = false;
    this.cdr.detectChanges();
  }


  emailMatchValidator(frm: FormGroup) {
    if (!!frm) {
      if (frm.get('email').value !== frm.get('confirmEmail').value) {
        return { emailNotMatch: true };
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  alternateEmailMatchValidator(frm: FormGroup) {
    if (!!frm) {
      if (
        frm.get('alternateEmail').value !==
        frm.get('confirmAlternateEmail').value
      ) {
        return { altEmailNotMatch: true };
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  resolved(captchaResponse: string) {
    this.captchaResp = captchaResponse;
  }

  getAllSpecialChars(str: string): string {
    const strAr = str.split('');
    const noSpecialChars = '';
    const resArr = [];
    for (const strItem of strAr) {
      if (/[^a-zA-Z0-9]/.test(strItem)) {
        resArr.push(strItem);
      }
    }
    if (resArr.length > 0) {
      return resArr.join('');
    } else {
      return noSpecialChars;
    }
  }

  ngOnDestroy() {
    this.userContactRoleSub?.unsubscribe();
    this.accountNoSub?.unsubscribe();
    this.userRegSub?.unsubscribe();
  }
}